/**
 * @packageDocumentation
 * @module f1/adt
 */

import { t_adt_open } from '../types';
import is_array from '../is/is_array';
import is_map from '../is/is_map';
import is_set from '../is/is_set';
import is_symbol from '../is/is_symbol';
import is_record from '../is/is_record';

export interface Copy {
  /**
   * Create a new (shallow) copy of a standard adt
   *
   * @public
   * @alias f1.copy
   * @param value
   * @example
   *
   * ```ts
   * const u = [1, [2, 5], 3, 4];
   * const rtn = copy([1, [2, 5], 3, 4]);
   * rtn !== [1, [2, 5], 3, 4]
   * rtn !== u
   * rtn[1] === u[1]
   * ```
   */
  <V extends t_adt_open>(value: V): V;
}

/**
 * @see Copy
 */
export default <V extends t_adt_open>(u: V): V => {
  if (is_array(u)) {
    return u.slice() as V;
  } else if (is_record(u)) {
    return { ...(u as Object) } as V;
  } else if (is_symbol(u)) {
    return Symbol(u.description) as V;
  } else if (is_map(u)) {
    return new Map(u) as V;
  } else if (is_set(u)) {
    return new Set(u) as V;
  } else {
    return u;
  }
};
